import type { TranslationSchema } from '@/locales/schema'

export const en: TranslationSchema = {
  common: {
    appName: 'Longbourn',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    showingResults: 'Showing {{start}}-{{end}} of {{total}} results',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    filter: 'Filter',
    sort: 'Sort',
    yes: 'Yes',
    no: 'No',
    optional: 'Optional',
    confirm: 'Confirm',
    by: 'by',
    setDate: 'set date',
    clearAll: 'Clear All',
    minimum: 'Minimum',
    maximum: 'Maximum',
    ascending: 'Ascending',
    descending: 'Descending',
    dateAdded: 'Date Added',
    exactlyStars: '{{stars}} stars',
    rangeStars: '{{min}} - {{max}} stars',
    noResultsMessage: 'No results found',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    apply: 'Apply',
    saving: 'Saving...',
  },
  errors: {
    general: 'An error occurred',
    userNotAuthenticated: 'User not authenticated',
    somethingWentWrong:
      'Something went wrong. Our team has been notified and we are working to fix the issue.',
    errorOccurred: 'An error occurred while processing your request',
    invalidISBN: 'Invalid ISBN format',
    isbnNotFound: 'No data found for this ISBN',
    networkError: 'Network error. Please check your connection and try again.',
    tryAgain: 'Try again',
    goBack: 'Go back home',
    technicalDetails: 'Technical details',
    devOnly: 'dev only',
  },
  auth: {
    signIn: 'Sign In',
    signOut: 'Sign Out',
    signUp: 'Sign Up',
    signInWithGoogle: 'Continue with Google',
    signInWithEmail: 'Sign In with Email',
    createAccount: 'Create Account',
    resetPassword: 'Reset Password',
    forgotPassword: 'Forgot your password?',
    backToSignIn: 'Back to Sign In',
    signingIn: 'Signing in...',
    signingOut: 'Signing out...',
    welcomeBack: 'Welcome back',
    yourPersonalLibrary: 'Your personal library companion',
    bySigningIn: 'By signing in, you agree to organize your reading journey',
    madeWithLove: 'Made with ❤️ for book lovers',
    signInError: 'Failed to sign in with Google. Please try again.',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    fullName: 'Full Name',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    fullNamePlaceholder: 'Enter your full name',
    emailPlaceholder: 'Enter your email address',
    passwordPlaceholder: 'Enter your password',
    confirmPasswordPlaceholder: 'Confirm your password',
    noAccount: "Don't have an account?",
    haveAccount: 'Already have an account?',
    emailRequired: 'Email is required',
    emailInvalid: 'Please enter a valid email address',
    passwordRequired: 'Password is required',
    passwordTooShort: 'Password must be at least 6 characters',
    nameRequired: 'Full name is required',
    passwordsDoNotMatch: 'Passwords do not match',
    invalidCredentials: 'Invalid email or password',
    emailNotConfirmed: 'Please check your email and confirm your account',
    userAlreadyExists: 'An account with this email already exists',
    generalError: 'An error occurred during authentication',
    setNewPassword: 'Set New Password',
    enterNewPassword: 'Enter your new password below',
    newPassword: 'New Password',
    confirmNewPassword: 'Confirm New Password',
    updatePassword: 'Update Password',
    passwordUpdated: 'Password Updated!',
    passwordUpdateSuccess: 'Your password has been successfully updated.',
    redirectingToLogin: 'Redirecting to login page...',
    checkYourEmail: 'Check Your Email',
    resetEmailSent: 'We sent a password reset link to {{email}}',
    resetEmailInstructions: 'Click the link in the email to reset your password.',
  },
  navigation: {
    dashboard: 'Dashboard',
    myBooks: 'My Books',
    digitalShelf: 'Digital Shelf',
    wishlist: 'Wishlist',
    bookRoulette: 'Book Roulette',
    billing: 'Billing',
    settings: 'Settings',
    notes: 'Notes',
    yearInReview: 'Year in Review',
    clubs: 'Clubs',
  },
  billing: {
    title: 'Billing',
    subtitle: 'Manage your subscription and payment method',
    loading: 'Loading billing information...',
    status: {
      title: 'Subscription Status',
      currentStatus: 'Current Status:',
      exemptAccess: 'You have exempt access to the system',
      exemptAs: 'as {{reason}}',
      expiresOn: 'Expires on: {{date}}',
      trialExpiresOn: 'Trial expires on:',
    },
    subscription: {
      title: 'Subscription Details',
      plan: 'Plan:',
      nextBilling: 'Next billing:',
      status: 'Status:',
      active: 'Active',
      monthly: 'Monthly',
      annual: 'Annual',
      cancelScheduled: 'Cancellation scheduled',
      cancelDate: 'Your subscription will be canceled on {{date}}',
    },
    manage: {
      title: 'Manage Subscription',
      description: 'Update payment method, download invoices or cancel your subscription',
      button: 'Manage Subscription',
      redirecting: 'Redirecting...',
    },
    upgrade: {
      title: 'Choose Your Plan',
      description: 'Unlock all Longbourn features',
      monthly: {
        title: 'Monthly',
        price: '{{price}}',
        period: '/month',
        cta: 'Choose Monthly',
      },
      annual: {
        title: 'Annual',
        price: '{{price}}',
        period: '/year',
        savings: 'Save {{amount}} ({{percent}}% off)',
        perMonth: '{{price}}/month',
        cta: 'Choose Annual',
        badge: 'Most Popular',
      },
      processing: 'Processing...',
      benefits: '✅ All features • 📊 Advanced analytics • 🎯 Priority support • 🚫 No limits',
      cancelAnytime: 'Cancel anytime',
    },
    badge: {
      loading: 'Loading...',
      exempt: 'Exempt - {{reason}}',
      exemptSpecial: 'Exempt - Special',
      annual: 'Annual Plan',
      monthly: 'Monthly Plan',
      trial: 'Trial - {{days}} day{{s}} remaining',
      trialExpired: 'Trial Expired',
      noAccess: 'No Access',
      // New cancellation statuses
      pastDue: 'Payment Past Due',
      canceled: 'Subscription Canceled',
      canceledWithAccess: '{{days}} days remaining',
      cancelingAtPeriodEnd: 'Canceling at Period End',
      trialing: 'Trial Period',
      inactive: 'Inactive',
      reasons: {
        beta_tester: 'Beta Tester',
        partner: 'Partner',
        lifetime: 'Lifetime',
        promotional: 'Promotional',
        staff: 'Staff',
        investor: 'Investor',
      },
    },
  },
  dashboard: {
    welcomeBack: 'Welcome back, {{name}}!',
    readingJourneyOverview: "Here's an overview of your reading journey",
    stats: {
      totalBooks: 'Total Books',
      booksRead: 'Books Read',
      readThisYear: 'Read This Year',
      currentlyReading: 'Currently Reading',
      wishlist: 'Wishlist',
      startAddingBooks: 'Start adding books to your library',
      percentOfCollection: '{{percent}}% of your collection',
      booksInProgress: 'Books in progress',
      booksToReadLater: 'Books to read later',
      ofTotal: 'of total',
      favoriteGenre: 'Your favorite genre is',
      noFavoriteGenre: 'Add genres to your books to discover your favorite',
      booksWithGenre: 'books',
      ownedPlusBorrowed_one: 'Yours only · +{{count}} borrowed not counted',
      ownedPlusBorrowed_other: 'Yours only · +{{count}} borrowed not counted',
      readIncludesBorrowed_one: 'Includes {{count}} borrowed book',
      readIncludesBorrowed_other: 'Includes {{count}} borrowed books',
    },
    analytics: {
      title: 'Analytics',
    },
    charts: {
      yearlyProgress: 'Yearly Progress',
      typeDistribution: 'Type Distribution',
      libraryStatus: 'Library Status',
      libraryStatusDescription: 'Overview of books by status and type',
      topGenres: 'Top Genres',
      topGenresDescription: 'Most common genres in your library',
      ratingDistribution: 'Rating Distribution',
      averageRating: 'Average',
      ratedBooks: 'rated books',
      noRatedBooks: 'No rated books yet',
      comingSoon: 'Coming soon...',
      booksReadIn: 'books read in',
      booksThisMonth: 'books this month',
      totalSoFar: 'total so far',
      books: 'books',
      booksTotal: 'books total',
      totalBooks: 'Total',
      booksRead: 'Read',
      percentageOfLibrary: 'Of library',
      percentage: 'Percentage',
      star: 'star',
      stars: 'stars',
      ofTotal: 'of total',
      noData: 'Not enough data to display chart',
      noDataTitle: 'No data available',
      addBooksToSeeDistribution: 'Add books to your library to see distribution',
      addBooksToSeeStatus: 'Add books to your library to see status',
      noGenresTitle: 'No genres found',
      addGenresToBooks: 'Add genres to your books to see statistics',
      noRatingsTitle: 'No ratings yet',
      rateYourBooks: 'Rate your read books to see statistics',
    },
  },
  books: {
    myBooks: 'My Books',
    yourCollection: 'Your book collection will appear here',
    booksCount: '{{count}} books in your library',
    wishlistCount: '{{count}} books in your wishlist',
    noBooksYet: 'No books yet',
    addBook: 'Add Book',
    editBook: 'Edit Book',
    deleteBook: 'Delete Book',
    confirmDelete: {
      title: 'Delete Book',
      message: 'Are you sure you want to delete "{{title}}"? This action cannot be undone.',
    },
    noBooks: 'No books in your library yet',
    addFirstBook: 'Add your first book',
    startAddingBooks: 'Start by adding some books to your collection',
    tryAdjustingFilters: 'Try adjusting your search or filters',
    bookAdded: 'Book added successfully',
    bookUpdated: 'Book updated successfully',
    bookDeleted: 'Book deleted successfully',
    fields: {
      title: 'Title',
      author: 'Author',
      isbn: 'ISBN',
      coverUrl: 'Cover Image URL',
      publishedYear: 'Published Year',
      pages: 'Pages',
      genre: 'Genre',
      status: 'Status',
      type: 'Type',
      rating: 'Rating',
      notes: 'Notes',
      genres: 'Genres',
      selectGenres: 'Select genres...',
      selectGenresHelp: 'Select genres that best describe the book',
      noGenresAvailable: 'No genres available',
      searchGenres: 'Search genres...',
      noGenresFound: 'No genres found',
      startDate: 'Started',
      endDate: 'Finished',
      borrowed: 'Borrowed book (not mine)',
      borrowedFrom: 'Borrowed from',
      borrowedFromPlaceholder: 'From whom? (optional)',
      borrowedHelp: 'Borrowed books count as reads but not toward your library total.',
    },
    isbn: {
      searching: 'Searching...',
      search: 'Search',
      bookFound: 'Book found! Data filled automatically. You can edit them below.',
      enterIsbnHelp: 'Enter ISBN to automatically fill title, author and other information',
      checkingDuplicate: 'Checking for duplicates...',
      duplicateFound: 'This ISBN is already registered for the book "{{title}}"',
    },
    scanner: {
      title: 'Scan Barcode',
      instructions: 'Point your camera at the ISBN barcode on the back of the book',
      scanning: 'Scanning for barcode...',
      startingCamera: 'Starting camera...',
      noCameraSupport: 'Camera not supported on this device',
      permissionDenied: 'Camera permission denied. Please allow camera access to scan barcodes.',
      noCameraFound: 'No camera found on this device',
      unknownError: 'An error occurred while accessing the camera',
      tryAgain: 'Try Again',
      scanBarcode: 'Scan Barcode',
      positionBarcode: 'Position the barcode within the rectangle',
      codeFound: 'Code found!',
      processing: 'Processing...',
    },
    status: {
      toRead: 'To Read',
      reading: 'Reading',
      read: 'Read',
      wishlist: 'Wishlist',
      unread: 'Unread',
    },
    type: {
      physical: 'Physical Book',
      digital: 'Digital Book',
    },
    actions: {
      title: 'Actions',
      rate: 'Rate read book',
      writeReview: 'Write review',
      yourNotes: 'Your notes',
      clickToWriteReview: 'Click to write your review',
      markAsReadToRate: 'Mark as read to change',
      removeRating: 'Remove rating',
      noRating: 'No rating',
      rateExperience: 'Rate your reading experience',
      oneStarRating: '⭐ 1 star',
      twoStarRating: '⭐⭐ 2 stars',
      threeStarRating: '⭐⭐⭐ 3 stars',
      fourStarRating: '⭐⭐⭐⭐ 4 stars',
      fiveStarRating: '⭐⭐⭐⭐⭐ 5 stars',
      addNotesPlaceholder: 'Add your notes about the book...',
      whenStartedReading: 'When you started reading this book',
      whenFinishedReading: 'When you finished reading this book',
      startReadingToday: 'Start reading today',
      markAsReadToday: 'Mark as read today',
      reread: 'Reread book',
      moveToWishlist: 'Move to wishlist',
      startReadingNow: 'Start reading now',
      acquiredBook: 'I got the book',
      moveToLibrary: 'Move to library',
      confirmDeleteTitle: 'Delete book',
      confirmDeleteMessage:
        'Are you sure you want to delete "{{title}}"? This action cannot be undone.',
      coverUrlHelp: 'Cover image URL (auto-filled with ISBN or you can enter manually)',
    },
    filters: {
      readInYear: 'Read in {{year}}',
      clearReadYear: 'Remove year filter',
    },
  },
  roulette: {
    title: 'Book Roulette',
    description: 'Let chance decide your next read',
    spin: 'Spin',
    createRoulette: 'Create Roulette',
    selectBooks: 'Select Books for Roulette',
    selectBooksDescription: 'Choose at least 1 book to create your reading roulette',
    minimumBooks: 'Select at least 1 book',
    booksSelected: 'selected',
    startRoulette: 'Start Roulette',
    noBooksFound: 'No books found',
    adjustFiltersToFindBooks: 'Try adjusting your filters to find books',
    spinning: 'Spinning',
    spinningDescription: 'The wheel is choosing your next adventure...',
    readyToSpin: 'Ready to spin!',
    booksInRoulette: '{{count}} books in the roulette',
    winner: 'Winner!',
    yourNextRead: 'Your next read is...',
    markAsNextRead: 'Mark as Next Read',
    startReadingNow: 'Start Reading Now',
    spinAgain: 'Spin Again',
    viewHistory: 'View History',
    funFact: 'The roulette has spoken! Time to start your reading journey.',
    history: 'Roulette History',
    historyDescription: 'Track your roulette spins and reading discoveries',
    backToRoulette: 'Back to Roulette',
    clearHistory: 'Clear History',
    noHistory: 'No roulette history yet',
    noHistoryDescription: 'Start spinning to build your reading roulette history!',
    startFirstRoulette: 'Start Your First Roulette',
    totalSpins: 'Total Spins',
    thisWeek: 'This Week',
    avgDuration: 'Avg Duration',
    uniqueBooks: 'Unique Books',
    allTime: 'All Time',
    last24Hours: 'Last 24 Hours',
    sortByDate: 'Sort by Date',
    sortByBook: 'Sort by Book',
    showingResults: 'Showing {{count}} of {{total}} results',
    spinDuration: 'Spin Duration',
    roulettePick: 'Roulette Pick',
    noResultsForFilter: 'No results match the current filter',
    // New persistent roulette translations
    myRoulettes: 'My Roulettes',
    manageRouletteCollections: 'Manage your saved roulette collections',
    createNew: 'Create New',
    noRoulettesYet: 'No roulettes yet',
    createFirstRouletteDescription:
      'Create your first themed roulette to organize your book selections by genre, mood, or any category you like.',
    createFirstRoulette: 'Create Your First Roulette',
    sortByUpdated: 'Recently Updated',
    sortByName: 'Name',
    sortBySpins: 'Most Spins',
    roulettesCount: 'roulettes',
    books: 'Books',
    spins: 'Spins',
    currentlyReading: 'Currently Reading',
    lastSpin: 'Last spin',
    needMinimumBooks: 'Add at least 1 book to spin',
    addMoreBooksToSpin: 'Add more books to enable spinning',
    // Create roulette specific translations
    createRouletteDescription: 'Create a themed collection of books for random selection',
    rouletteConfiguration: 'Roulette Configuration',
    rouletteName: 'Roulette Name',
    selectBooksMessage: 'Select Books ({{count}} selected)',
    minimumBooksWarning: '⚠️ Select at least 1 book to create a roulette',
    nameRequired: 'Name *',
    namePlaceholder: 'e.g., Science Fiction Classics',
    descriptionOptional: 'Description',
    descriptionPlaceholder: 'Optional description for this roulette...',
    colorTheme: 'Color Theme',
    icon: 'Icon',
    preview: 'Preview',
    readyToCreate: '✓ Ready to create',
    creating: 'Creating...',
    createRouletteButton: 'Create Roulette',
    // History specific translations
    errorLoadingHistory: 'Error loading history',
    backToRoulettes: 'Back to Roulettes',
    last24HoursFilter: 'Last 24 Hours',
    noSpinsLast24Hours: 'No spins in the last 24 hours',
    spinLabel: 'spin',
    // Roulette result translations
    newRouletteButton: 'New Roulette',
    // Pagination
    previous: 'Previous',
    next: 'Next',
    pageOf: 'Page {{page}} of {{total}}',
    // Error messages
    errorLoadingBooks: 'Error loading books',
    noBooksMessage: 'No books found',
    adjustFiltersMessage: 'Try adjusting your filters',
    // Delete roulette
    editRoulette: 'Edit Roulette',
    deleteRoulette: 'Delete Roulette',
    deleteRouletteTitle: 'Delete Roulette',
    deleteRouletteMessage:
      'Are you sure you want to delete the roulette "{{name}}"? This action cannot be undone.',
    // Progress and completion
    availableBooks: 'Available',
    spinsCompleted: 'Completed',
    progress: 'Progress',
    completed: 'Completed',
    allBooksSpun: 'All books have been selected',
    noMoreBooksToSpin: 'No more books available to spin',
    allBooksAlreadySpun: 'All books have already been spun',
    alreadySelected: 'already selected',
    // Progress page translations
    viewProgress: 'View Progress',
    progressDescription: 'Track your roulette progress and spin whenever you want',
    spinWheel: 'Spin Wheel',
    spinRoulette: 'Spin Roulette',
    backToList: 'Back to List',
    suggestBooks: 'Suggest books',
    suggestHint: 'Picks unread books from the genres and authors you rated best',
    suggestedBooks_one: '{{count}} book suggested and selected based on your ratings.',
    suggestedBooks_other: '{{count}} books suggested and selected based on your ratings.',
    noSuggestions: 'No unread books to suggest.',
  },
  search: {
    title: 'Search Books',
    description: 'Search through your library',
    placeholder: 'Search by title, author or ISBN...',
    searchByTitleAuthor: 'Search by title or author...',
    noResults: 'No results found',
    results: '{{count}} result(s) found',
  },
  statistics: {
    title: 'Reading Statistics',
    description: 'Your reading insights and patterns',
    booksPerMonth: 'Books per Month',
    genreDistribution: 'Genre Distribution',
    readingGoal: 'Reading Goal',
    averageRating: 'Average Rating',
    totalPages: 'Total Pages Read',
  },
  wishlist: {
    title: 'My Wishlist',
    description: 'Books you want to read in the future',
    addToWishlist: 'Add to Wishlist',
    addFirstBook: 'Add your first wishlist book',
    emptyMessage: 'Your wishlist is empty',
  },
  digitalShelf: {
    title: 'Digital Shelf',
    yourCollection: 'Your digital book collection will appear here',
    booksCount: '{{count}} digital books in your library',
    noBooks: 'No digital books yet',
    addBook: 'Add Digital Book',
    editBook: 'Edit Digital Book',
    addFirstBook: 'Add your first digital book',
    startAddingBooks: 'Start by adding some digital books to your collection',
  },
  settings: {
    description: 'Manage your account preferences and application settings',
    quickActions: 'Quick Actions',
    comingSoon: 'Coming Soon',
    sectionNotFound: 'Settings section not found',
    sections: {
      preferredGenres: {
        description: 'Manage your favorite genres',
      },
      account: {
        title: 'Account',
        description: 'Profile and account settings',
      },
      notifications: {
        title: 'Notifications',
        description: 'Manage notification preferences',
      },
      appearance: {
        title: 'Appearance',
        description: 'Themes and visual preferences',
      },
    },
    preferredGenres: {
      title: 'Preferred Genres',
      description: 'Configure your favorite genres for a personalized experience',
      selectedCount: '{{count}} genres selected',
      smartSuggestions: 'Smart Suggestions',
      suggestionsHelp: 'Based on books in your current library',
      searchPlaceholder: 'Search genres...',
      suggestions: 'Suggestions',
      allGenres: 'All Genres',
      both: 'Both',
      basedOnLibrary: 'Based on Your Library',
      otherGenres: 'Other Genres',
      clearAll: 'Clear All',
      selectedSummary: '{{count}} genres selected',
      noGenresSelected: 'No genres selected',
      selectAll: 'Select All',
    },
  },
  landing: {
    hero: {
      freeTrial: '🎁 14 days free',
      title: 'Your Smart',
      subtitle: 'Library',
      description:
        "Never forget a book you've read. Discover fascinating insights about your reading habits. Let destiny choose your next literary adventure.",
      startFreeTrial: 'Start Free Trial',
      seeHowItWorks: 'See How It Works',
      dashboardPreview: 'Dashboard Preview',
    },
    features: {
      title: 'Everything You Need for Your Reading Journey',
      subtitle: 'Powerful features designed for book lovers',
      bookRoulette: {
        title: 'Book Roulette',
        badge: 'UNIQUE',
        description:
          "Can't decide what to read next? Let our unique Book Roulette choose your next adventure randomly! Create themed roulettes and add an element of surprise to your reading.",
      },
      smartAnalytics: {
        title: 'Smart Analytics',
        description:
          'Beautiful charts and insights about your reading patterns, favorite genres, progress over time and detailed statistics from your personal library.',
      },
      barcodeScanner: {
        title: 'Barcode Scanner',
        description:
          'Add books instantly by scanning ISBN barcodes. Book details filled automatically from our comprehensive database.',
      },
      smartOrganization: {
        title: 'Smart Organization',
        description:
          'Organize books by status, genres, ratings. Track reading dates, add personal notes and keep your library always organized.',
      },
      readingProgress: {
        title: 'Reading Progress',
        description:
          "Track what you're reading, want to read and have completed with detailed progress monitoring and customizable reading goals.",
      },
      multiLanguage: {
        title: 'Multi-language',
        description:
          'Available in Portuguese, English, and Spanish. Perfect for international book lovers who read in different languages.',
      },
    },
    howItWorks: {
      title: 'How Longbourn Works',
      subtitle: 'Get started in three simple steps',
      step1: {
        title: 'Add Your Books',
        description:
          'Scan barcodes, search by ISBN, or add books manually to build your digital library.',
      },
      step2: {
        title: 'Organize & Analyze',
        description:
          'Track your reading progress and discover insights about your habits with beautiful and detailed analytics.',
      },
      step3: {
        title: "Discover What's Next",
        description:
          'Use Book Roulette to let chance decide your next read, or browse your organized library.',
      },
    },
    pricing: {
      badge: '🎁 14 days free to try',
      title: 'Choose Your Plan',
      subtitle: 'Start with 14 free days. Cancel anytime.',
      monthly: {
        title: 'Monthly Plan',
        price: '$9.99',
        period: '/month',
        description: 'Total flexibility with monthly payment',
        cta: 'Start Free Trial',
      },
      annual: {
        title: 'Annual Plan',
        price: '$34.99',
        period: '/year',
        originalPrice: '$119.88',
        savings: 'Save $84.89',
        description: 'Best value! Only {{perMonth}} per month',
        discount: '71% OFF',
        cta: 'Start Free Trial',
        prioritySupport: '💎 Priority support access',
      },
      features: {
        freeTrial: '✨ 14 days free trial',
        unlimitedLibrary: 'Unlimited book library',
        bookRoulette: 'Exclusive Book Roulette',
        barcodeScanner: 'Barcode scanner',
        detailedAnalytics: 'Detailed analytics',
        multiLanguage: 'Support for 3 languages',
      },
    },
    spotlight: {
      badge: '🎲 UNIQUE FEATURE',
      title: 'Let Destiny Choose Your Next',
      titleHighlight: ' Adventure',
      description:
        'Our Book Roulette is the only feature of its kind. Create themed roulettes, spin the wheel, and discover your next great read. Perfect for indecisive readers!',
      features: {
        customRoulettes: 'Create custom themed roulettes by genre, mood, or category',
        trackHistory: 'Track your roulette history and spinning statistics',
        neverWorryAgain: 'Never worry about choosing your next book again',
      },
      interactiveRoulette: 'Interactive Roulette',
      demoText: 'Demo available after signup',
    },
    cta: {
      title: 'Ready to Transform Your Reading Experience?',
      description:
        'Join thousands of book lovers who have already organized their reading journey with Longbourn.',
      button: 'Start Free Trial',
      benefits: '🎁 14 days free • 🚫 No credit card • ⚡ Cancel anytime',
    },
    footer: {
      madeWith: 'Made with ❤️ for book lovers everywhere',
      privacy: 'Privacy',
      terms: 'Terms',
    },
  },
  insights: {
    title: 'Insights',
    seeAll: 'See all',
    noBooksYet: 'Add books to see this insight',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
    bookCount_one: '{{count}} book',
    bookCount_other: '{{count}} books',
    missingDates: {
      title_one: '{{count}} read book without a finish date',
      title_other: '{{count}} read books without a finish date',
      description:
        'Without a date they don\'t show up in "Read This Year" or the Yearly Progress chart.',
      fix: 'Fill in dates',
      modalTitle: 'When did you finish?',
      modalHint: 'We suggest the date each book was last updated. Adjust if needed.',
    },
    continueReading: {
      title: 'Continue reading',
      empty: 'No books in progress. Why not start one?',
      noStartDate: 'No start date',
      startedToday: 'Started today',
      daysReading_one: 'Reading for {{count}} day',
      daysReading_other: 'Reading for {{count}} days',
    },
    goal: {
      title: '{{year}} goal',
      empty: 'Set how many books you want to read in {{year}}.',
      set: 'Set goal',
      edit: 'Edit goal',
      remove: 'Remove goal',
      howMany: 'How many books in {{year}}?',
      reached: 'Goal reached! 🎉',
      onTrack: "You're right on track.",
      ahead_one: '{{count}} book ahead of pace',
      ahead_other: '{{count}} books ahead of pace',
      behind_one: '{{count}} book behind pace',
      behind_other: '{{count}} books behind pace',
    },
    streak: {
      title: 'Reading streak',
      months_one: '{{count}} month',
      months_other: '{{count}} months',
      start: 'Finish a book this month to start a streak.',
      active: "You've already finished a book this month. 🔥",
      keepAlive: 'Finish a book this month to keep it going.',
      longest_one: 'Best: {{count}} month',
      longest_other: 'Best: {{count}} months',
    },
    readingTime: {
      title: 'Reading time',
      basedOn_one: 'Based on {{count}} book with dates',
      basedOn_other: 'Based on {{count}} books with dates',
      empty: 'Add start and finish dates to read books to see your pace.',
      perBook: 'per book',
      fastest: 'Fastest',
      slowest: 'Slowest',
    },
    topAuthors: {
      title: 'Top authors',
      description: 'Authors with the most books in your library',
      readOfTotal: '{{read}}/{{total}} read',
    },
    backlog: {
      title: 'Backlog by genre',
      description: "Where the books you own but haven't read are",
      empty: 'Add genres to your books to see this insight.',
      unreadOfOwned: '{{unread}} of {{owned}} unread',
    },
    pace: {
      title: 'Buying vs. reading',
      added: 'Added',
      finished: 'Finished',
      noActivity: 'No activity in the last 12 months',
      buyingMore: 'Last 12 months: {{added}} added, {{finished}} finished. The pile is growing.',
      readingMore: "Last 12 months: {{added}} added, {{finished}} finished. You're keeping up!",
    },
    stalled: {
      title: 'Stalled books',
      description: 'In "Reading" for more than {{count}} days',
      pause: 'Pause',
      pauseHint: 'Moves the book back to "To read"',
    },
    preferred: {
      title: 'Taste vs. reading',
      description: 'Genres you say you like compared with what you read',
      noPreferences: "You haven't picked preferred genres yet.",
      setPreferences: 'Pick now',
      preferredTag: 'Preferred genre',
      readCount_one: '{{count}} read',
      readCount_other: '{{count}} read',
      surprise: "You read a lot of {{genre}}, but it isn't in your preferred genres.",
      neglected: "{{genre}} is a preferred genre, but you haven't read any yet.",
    },
    yearInReview: {
      cta: 'Year in review',
      title: '{{year}} in review',
      subtitle: 'Your reading year in numbers',
      selectYear: 'Choose year',
      share: 'Share image',
      generating: 'Generating...',
      downloaded: 'Image downloaded!',
      imageTitle: 'My reading year',
      imageBooksRead_one: 'book read',
      imageBooksRead_other: 'books read',
      empty: 'No books finished in {{year}} yet.',
      booksRead: 'Books read',
      booksAdded: 'Books added',
      averageRating: 'Average rating',
      physicalDigital: 'Physical / Digital',
      topGenre: 'Genre of the year',
      topAuthor: 'Author of the year',
      bestRated: 'Top rated',
      fastest: 'Fastest read',
      busiestMonth: 'Busiest month',
      averageTime: 'Average time',
      allBooks: 'Every book of the year',
      shareTitle: '📚 My {{year}} reading year',
    },
    notes: {
      title: 'Notes',
      subtitle_one: '{{count}} book with notes',
      subtitle_other: '{{count}} books with notes',
      searchPlaceholder: 'Search notes, titles and authors...',
      empty: "You haven't written any notes yet. Add notes to your books and they'll show up here.",
    },
  },
  clubs: {
    title: 'Book clubs',
    subtitle: 'Read together with friends: challenges, loans and traveling books',
    create: 'Create club',
    edit: 'Edit club',
    memberCount_one: '{{count}} member',
    memberCount_other: '{{count}} members',
    notFound: "Club not found or you're not a member.",
    backToClubs: 'Back to clubs',
    leave: 'Leave club',
    leaveTitle: 'Leave club?',
    leaveMessage: 'You will leave "{{club}}". You\'ll need a new invite to come back.',
    deleteTitle: 'Delete club?',
    deleteMessage: '"{{club}}" and all its challenges will be deleted for every member.',
    empty: {
      title: "You're not in any club yet",
      description: 'Create a club and invite your friends, or open an invite link you received.',
    },
    form: {
      name: 'Club name',
      namePlaceholder: 'e.g. Saturday Readers',
      description: 'Description',
      descriptionPlaceholder: 'Monthly meetups, rules, anything…',
      create: 'Create',
    },
    roles: {
      admin: 'Admin',
      member: 'Member',
    },
    members: {
      title_one: '{{count}} member',
      title_other: '{{count}} members',
      you: 'you',
      makeAdmin: 'Make admin',
      makeMember: 'Remove admin',
      remove: 'Remove',
      removeTitle: 'Remove member?',
      removeMessage: '{{name}} will be removed from the club.',
    },
    invite: {
      title: 'Invite',
      description: 'Create a link and send it to whoever you want in the club.',
      create: 'Create invite link',
      share: 'Share link',
      copied: 'Link copied!',
      revoke: 'Disable link',
      expires: 'Valid until {{date}}',
      shareText: 'Join "{{club}}" on Longbourn',
    },
    join: {
      invitedBy: '{{name}} invited you to',
      accept: 'Join club',
      open: 'Open club',
      invalid: 'Invalid or expired invite',
      invalidHint: 'Ask someone in the club for a new link.',
    },
  },
  challenges: {
    title: 'Challenges',
    create: 'New challenge',
    edit: 'Edit challenge',
    empty: 'No challenges yet. Create one and everyone suggests categories!',
    notFound: 'Challenge not found.',
    formerMember: 'Former member',
    categoryCount_one: '{{count}} category',
    categoryCount_other: '{{count}} categories',
    categoryProgress: '{{count}} of {{total}} categories set',
    myProgress: 'You: {{done}}/{{total}}',
    deleteTitle: 'Delete challenge?',
    deleteMessage: '"{{name}}", its categories, picks and comments will be deleted for everyone.',
    phase: {
      upcoming: 'Upcoming',
      active: 'In progress',
      finished: 'Finished',
    },
    legend: {
      done: 'Read in period',
      picked: 'Picked',
    },
    filter: {
      all: 'All categories',
      todo: 'No book from me',
      picked: 'Picked, not read yet',
      done: 'Done by me',
    },
    form: {
      defaultName: '{{year}} Challenge',
      name: 'Name',
      startsOn: 'Starts',
      endsOn: 'Ends',
      datesInvalid: 'The end must be after the start.',
      periodHint: 'Only books finished within this period count.',
      target: 'Total categories',
      targetHint: 'e.g. 33 for 3 people suggesting 11 each.',
      description: 'Rules',
      descriptionPlaceholder: 'Group agreements, prizes, deadlines…',
    },
    categories: {
      title: 'Categories',
      empty: 'No categories yet. Suggest the first one above!',
      suggest: 'Suggest a category',
      titlePlaceholder: 'e.g. Book with a white cover',
      descriptionPlaceholder: 'Details (optional)',
      quota: 'You suggested {{done}} of {{quota}}',
      similar: 'A similar one already exists: "{{title}}"',
      suggestedBy: 'Suggested by {{name}}',
      by: 'by {{name}}',
      everyonesPicks: 'Picks ({{count}})',
      noPicks: 'Nobody picked a book yet.',
      deleteTitle: 'Delete category?',
      deleteMessage_one: 'The category and {{count}} pick will be deleted.',
      deleteMessage_other: 'The category and {{count}} picks will be deleted.',
    },
    entry: {
      mine: 'My book for this category',
      pickBook: 'Choose a book from your library',
      noBooks: 'No books available',
      pick: 'Pick',
      addNewBook: 'Add a new book',
      newBook: 'New book (becomes your pick)',
      change: 'Change book',
      remove: 'Remove',
      markReadToday: 'Mark as read today',
      outsidePeriodHint: 'Today is outside the challenge period',
      readOn: 'read on {{date}}',
      readOutsidePeriod: "Read on {{date}}, outside the period, doesn't count",
      reading: 'Reading',
      notRead: 'Not read yet',
      picked: 'picked',
      done: 'read',
      none: 'no book',
      bookDeleted: 'This book was removed from your library.',
    },
    comments: {
      count_one: '{{count}} comment',
      count_other: '{{count}} comments',
      hide: 'Hide comments',
      empty: 'No comments yet.',
      placeholder: 'Write a comment…',
      send: 'Send',
      add: 'Comment',
    },
  },
  loans: {
    lend: 'Lend',
    lendTitle: 'Lend "{{title}}"',
    borrower: 'To whom',
    chooseFriend: 'Choose someone from your clubs',
    someoneElse: 'Someone else (not on the app)',
    friendHint:
      'The book shows up in their library as borrowed, and reading it counts in their stats.',
    borrowerName: 'Name',
    borrowerNamePlaceholder: 'e.g. Aunt Lucy',
    lentOn: 'Lent on',
    lentTo: 'With {{name}} since {{date}}',
    borrowedFrom: 'Borrowed from {{name}}',
    borrowed: 'Borrowed',
    returned: 'returned',
    markReturned: 'Mark as returned',
    cardTitle: 'Loans',
    withMe_one: 'With you ({{count}})',
    withMe_other: 'With you ({{count}})',
    lentOut_one: 'You lent ({{count}})',
    lentOut_other: 'You lent ({{count}})',
    from: 'from {{name}}',
    to: 'with {{name}}',
    since: 'since {{date}}',
  },
  traveling: {
    title: 'Traveling books',
    create: 'New traveling book',
    empty: 'No books traveling. Send one of yours from hand to hand!',
    book: 'Book',
    chooseBook: 'Choose one of your books',
    noBooks: 'None of your books available',
    route: 'Route',
    routeHint: 'Tap people in the order the book will travel. At the end it comes back to you.',
    noReaders: 'Invite someone to the club first.',
    note: 'Note for the readers',
    notePlaceholder: 'e.g. careful with the cover, pencil notes are fine…',
    start: 'Send it traveling',
    ownedBy: "{{name}}'s book",
    traveling: 'Traveling',
    home: 'Back home',
    backHome: '🏠 Back home on {{date}}',
    withReaderToday: 'With {{name}} since today',
    withReader_one: 'With {{name}} for {{count}} day',
    withReader_other: 'With {{name}} for {{count}} days',
    next: 'next: {{name}}',
    nextHome: 'then back to the owner',
    pass: 'Pass it on',
    passTo: 'Hand to {{name}}',
    sendHome: 'Return to the owner',
    passNotePlaceholder: 'Leave a note for the next readers (optional)',
    finished_one: '{{count}} finished trip',
    finished_other: '{{count}} finished trips',
  },
}
